import React, { useState } from 'react';

import i18n from 'i18n-js';

import { withStyles } from '@material-ui/core/styles';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import { IconButton } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import PaletteIcon from '@material-ui/icons/Palette';

import { SectionData, MenuDetailsData } from 'store/menuDetailsStore/reducer';
import { MenuType, Positions } from 'constants/enums';
import { theme } from 'config/theme';
import { PopupConfirmation, ColorSelector } from 'components/shared';
import RenameSection from './RenameSectionMenuItem';
import SectionImageMenuItem from './SectionImageMenuItem';
import { AddNewDishItem } from './AddNewDishItem';
import DeleteMenuItem from './DeleteMenuItem';
import DisableEnableSection from './DisableEnableSection';
import EditDailyMenuSection from './EditDailyMenuSection';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import menuDetailsStore from 'store/menuDetailsStore';

//should be refactored and replaced with shared basic three dots menu component
const StyledMenu = withStyles((theme) => ({
    paper: {
        border: '1px solid #d3d4d5',
        '& ul.MuiList-root': {
            padding: 0,
        },
        '& ul > li:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
    },
}))((props: MenuProps) => (
    <Menu
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
));

export interface ThreeDotsSectionProps {
    section: SectionData;
    onDeleteSectionClick: () => void;
    onChooseExisting: () => void;
    onChooseFromCatalog: () => void;
    menu: MenuDetailsData;
    color: string;
    onChangeColor: (color: string) => void;
    onSaveColor: () => void;
    onResetColor: () => void;
}

const StyledMenuItem = withStyles((theme) => ({
    root: {
        minHeight: '48px !important',
        '&:focus': {
            backgroundColor: theme.palette.primary.main,
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                color: theme.palette.common.white,
            },
        },
    },
}))(MenuItem);

export default function ThreeDotsSectionMenu(props: ThreeDotsSectionProps) {
    const { menu, section } = props;
    const dispatch = useDispatch();
    const user = useSelector((state: ApplicationState) => state.user);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showColorSelector, setShowColorSelector] = useState(false);
    const isCatalogManager =
        menu.type === MenuType.Catalog &&
        menu.catalogData?.allowToEditCatalog &&
        currentRestaurant?.currentPosition === Positions.MANAGER;
    const restaurantName = user.restaurants.find((i) => i.id === user.currentRestaurantId)?.name;
    const menus = useSelector((state: ApplicationState) => state.menus.menus);

    const isDailyMenu = menu.type === MenuType.Daily;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        event.stopPropagation();
    };

    const handleClose = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setAnchorEl(null);
        event?.stopPropagation();
    };

    function onChooseExistingClick(e: any) {
        setAnchorEl(null);
        e.stopPropagation();
        props.onChooseExisting();
    }

    function onChooseFromCatalogClick(e: any) {
        setAnchorEl(null);
        e.stopPropagation();
        props.onChooseFromCatalog();
    }

    function handleColorSelector(event: React.MouseEvent<HTMLElement>) {
        setAnchorEl(null);
        event.stopPropagation();
        setShowColorSelector(true);
    }

    function handleDeleteClick() {
        setAnchorEl(null);
        setShowConfirmation(true);
    }

    function handleColorReset() {
        props.onResetColor();
        setShowColorSelector(false);
    }

    function prepareDeleteTitle() {
        switch (menu.type) {
            case MenuType.Daily:
                return i18n.t('common.deleteMenu');
            case MenuType.MultiCourse:
                return i18n.t('common.deleteCourse');
            default:
                return i18n.t('common.deleteSection');
        }
    }

    function prepareConfirmationMessage() {
        switch (menu.type) {
            case MenuType.Daily:
                return i18n.t('confirmation.deleteMenu');
            case MenuType.MultiCourse:
                return i18n.t('confirmation.deleteCourse');
            default:
                return i18n.t('confirmation.deleteSection');
        }
    }

    function prepareContent() {
        switch (menu.type) {
            case MenuType.Daily: {
                const content = [];
                if (props.section?.dishes.length < 5) {
                    content.push(
                        <AddNewDishItem
                            key="add-dish-item"
                            sectionId={props.section?.id}
                            sections={props.menu.sections}
                            close={handleClose}
                            forThreeDotsMenu={true}
                            dailyMenuId={props.section?.id}
                        />,
                        <StyledMenuItem onClick={onChooseExistingClick} key="chose-dish-item">
                            <FormatListBulletedIcon
                                fontSize="small"
                                color="disabled"
                                style={{ marginRight: 23 }}
                            />
                            <ListItemText primary={i18n.t('common.chooseExisting')} />
                        </StyledMenuItem>
                    );
                }
                menus.some((x) => x.type === MenuType.Catalog) &&
                    content.push(
                        <StyledMenuItem
                            onClick={onChooseFromCatalogClick}
                            key="copy-dish-from-catalog"
                        >
                            <FormatListBulletedIcon
                                fontSize="small"
                                color="disabled"
                                style={{ marginRight: 23 }}
                            />
                            <ListItemText primary={i18n.t('common.copyFromCatalog')} />
                        </StyledMenuItem>
                    );
                content.push(
                    <EditDailyMenuSection
                        key="edit-menu"
                        section={props.section}
                        onClose={handleClose}
                        menuType={menu.type}
                        menus={props.menu.sections.reduce((result: string[], current) => {
                            if (current.id !== props.section?.id) {
                                result.push(current.name.toUpperCase());
                            }
                            return result;
                        }, [])}
                    />,
                    <StyledMenuItem onClick={handleColorSelector} key="set-color">
                        <PaletteIcon
                            fontSize="small"
                            color="disabled"
                            style={{ marginRight: 23 }}
                        />
                        <ListItemText primary={i18n.t('common.changeColor')} />
                    </StyledMenuItem>,
                    <DisableEnableSection
                        key="disable-section"
                        isDisabled={props.section?.isDisabled}
                        id={props.section?.id}
                        onClose={handleClose}
                        onToggleDisableSection={(id: string) => {
                            dispatch(menuDetailsStore.actionCreators.disableEnableSection(id));
                        }}
                    />,
                    <DeleteMenuItem
                        onDeleteClick={handleDeleteClick}
                        key="delete-dish-item"
                        text={isDailyMenu ? i18n.t('common.deleteMenu') : ''}
                    />
                );
                return content;
            }
            case MenuType.MultiCourse: {
                const content = [
                    <AddNewDishItem
                        key="add-dish-item"
                        sectionId={props.section?.id}
                        sections={props.menu.sections}
                        close={handleClose}
                        forThreeDotsMenu={true}
                        dailyMenuId={props.section?.id}
                    />,
                    <StyledMenuItem onClick={onChooseExistingClick} key="chose-dish-item">
                        <FormatListBulletedIcon
                            fontSize="small"
                            color="disabled"
                            style={{ marginRight: 23 }}
                        />
                        <ListItemText primary={i18n.t('common.chooseExisting')} />
                    </StyledMenuItem>,
                ];
                menus.some((x) => x.type === MenuType.Catalog) &&
                    content.push(
                        <StyledMenuItem
                            onClick={onChooseFromCatalogClick}
                            key="copy-dish-from-catalog"
                        >
                            <FormatListBulletedIcon
                                fontSize="small"
                                color="disabled"
                                style={{ marginRight: 23 }}
                            />
                            <ListItemText primary={i18n.t('common.copyFromCatalog')} />
                        </StyledMenuItem>
                    );
                content.push(
                    <EditDailyMenuSection
                        key="edit-menu"
                        menuType={menu.type}
                        section={props.section}
                        onClose={handleClose}
                        menus={props.menu.sections.reduce((result: string[], current) => {
                            if (current.id !== props.section?.id) {
                                result.push(current.name.toUpperCase());
                            }
                            return result;
                        }, [])}
                    />,
                    <StyledMenuItem onClick={handleColorSelector} key="set-color">
                        <PaletteIcon
                            fontSize="small"
                            color="disabled"
                            style={{ marginRight: 23 }}
                        />
                        <ListItemText primary={i18n.t('common.changeColor')} />
                    </StyledMenuItem>,
                    <DisableEnableSection
                        key="disable-section"
                        isDisabled={props.section?.isDisabled}
                        id={props.section?.id}
                        onClose={handleClose}
                        onToggleDisableSection={(id: string) => {
                            dispatch(menuDetailsStore.actionCreators.disableEnableSection(id));
                        }}
                        isCourse={true}
                    />,
                    <DeleteMenuItem
                        onDeleteClick={handleDeleteClick}
                        key="delete-dish-item"
                        text={
                            menu.type === MenuType.MultiCourse
                                ? i18n.t('common.deleteCourse')
                                : i18n.t('common.deleteSection')
                        }
                    />
                );
                return content;
            }
            case MenuType.Catalog: {
                const content = [];
                if (isCatalogManager) {
                    content.push(
                        <AddNewDishItem
                            key="add-dish-item"
                            sectionId={props.section?.id}
                            sections={props.menu.sections}
                            close={handleClose}
                            forThreeDotsMenu={true}
                            dailyMenuId=""
                        />,
                        // <StyledMenuItem onClick={onChooseExistingClick} key="chose-dish-item">
                        //     <FormatListBulletedIcon
                        //         fontSize="small"
                        //         color="disabled"
                        //         style={{ marginRight: 23 }}
                        //     />
                        //     <ListItemText primary={i18n.t('common.chooseExisting')} />
                        // </StyledMenuItem>,
                        <RenameSection
                            key="rename-section"
                            sectionName={props.section?.name}
                            sectionId={props.section?.id}
                            close={handleClose}
                            sections={props.menu.sections.reduce((result: string[], current) => {
                                if (current.name !== props.section?.name) {
                                    result.push(current.name.toUpperCase());
                                }
                                return result;
                            }, [])}
                        />,
                        currentRestaurant?.currentPosition === Positions.MANAGER ? (
                            <StyledMenuItem onClick={handleColorSelector} key="set-color">
                                <PaletteIcon
                                    fontSize="small"
                                    color="disabled"
                                    style={{ marginRight: 23 }}
                                />
                                <ListItemText primary={i18n.t('common.changeColor')} />
                            </StyledMenuItem>
                        ) : null,
                        <SectionImageMenuItem
                            sectionId={props.section.id}
                            photo={props.section.photo}
                            close={handleClose}
                            key="set-image"
                        />,
                        <DisableEnableSection
                            key="disable-section"
                            title={
                                props.section?.catalogData!.isDisabled
                                    ? i18n.t('restaurant.enableForRestaurant', {
                                          name: i18n.t('common.group.one'),
                                      })
                                    : i18n.t('restaurant.disableForRestaurant', {
                                          name: i18n.t('common.group.one'),
                                      })
                            }
                            isDisabled={props.section?.catalogData!.isDisabled}
                            id={props.section?.id}
                            onClose={handleClose}
                            onToggleDisableSection={(id) => {
                                dispatch(menuDetailsStore.actionCreators.disableEnableSection(id));
                            }}
                        />,
                        !section.catalogData?.isDisabled && (
                            <DisableEnableSection
                                key="disable-section-for-restaurant"
                                title={
                                    props.section?.isDisabled
                                        ? i18n.t('restaurant.enableForRestaurant', {
                                              name: restaurantName,
                                          })
                                        : i18n.t('restaurant.disableForRestaurant', {
                                              name: restaurantName,
                                          })
                                }
                                isDisabled={props.section?.isDisabled}
                                id={props.section?.id}
                                onClose={handleClose}
                                onToggleDisableSection={(_) => {
                                    if (
                                        currentRestaurant?.id &&
                                        currentRestaurant?.currentPosition === Positions.MANAGER
                                    ) {
                                        dispatch(
                                            menuDetailsStore.actionCreators.restaurantCatalogSectionToggleDisable(
                                                currentRestaurant.id,
                                                currentRestaurant.currentPosition,
                                                props.section
                                            )
                                        );
                                    }
                                }}
                            />
                        ),
                        <DeleteMenuItem onDeleteClick={handleDeleteClick} key="delete-dish-item" />
                    );
                } else {
                    content.push(
                        <DisableEnableSection
                            key="disable-section"
                            isDisabled={props.section?.isDisabled}
                            id={props.section?.id}
                            onClose={handleClose}
                            onToggleDisableSection={(id: string) => {
                                if (
                                    currentRestaurant?.id &&
                                    currentRestaurant?.currentPosition === Positions.MANAGER
                                ) {
                                    dispatch(
                                        menuDetailsStore.actionCreators.restaurantCatalogSectionToggleDisable(
                                            currentRestaurant.id,
                                            currentRestaurant.currentPosition,
                                            props.section
                                        )
                                    );
                                }
                            }}
                        />
                    );
                }

                return content;
            }
            default: {
                const content = [];
                content.push(
                    <AddNewDishItem
                        key="add-dish-item"
                        sectionId={props.section?.id}
                        sections={props.menu.sections}
                        close={handleClose}
                        forThreeDotsMenu={true}
                        dailyMenuId=""
                    />,
                    <StyledMenuItem onClick={onChooseExistingClick} key="chose-dish-item">
                        <FormatListBulletedIcon
                            fontSize="small"
                            color="disabled"
                            style={{ marginRight: 23 }}
                        />
                        <ListItemText primary={i18n.t('common.chooseExisting')} />
                    </StyledMenuItem>,
                    menus.some((x) => x.type === MenuType.Catalog) ? (
                        <StyledMenuItem
                            onClick={onChooseFromCatalogClick}
                            key="copy-dishfrom-catalog"
                        >
                            <FormatListBulletedIcon
                                fontSize="small"
                                color="disabled"
                                style={{ marginRight: 23 }}
                            />
                            <ListItemText primary={i18n.t('common.copyFromCatalog')} />
                        </StyledMenuItem>
                    ) : null,
                    <RenameSection
                        key="rename-section"
                        sectionName={props.section?.name}
                        sectionId={props.section?.id}
                        close={handleClose}
                        sections={props.menu.sections.reduce((result: string[], current) => {
                            if (current.name !== props.section?.name) {
                                result.push(current.name.toUpperCase());
                            }
                            return result;
                        }, [])}
                    />,
                    currentRestaurant?.currentPosition === Positions.MANAGER ? (
                        <StyledMenuItem onClick={handleColorSelector} key="set-color">
                            <PaletteIcon
                                fontSize="small"
                                color="disabled"
                                style={{ marginRight: 23 }}
                            />
                            <ListItemText primary={i18n.t('common.changeColor')} />
                        </StyledMenuItem>
                    ) : null,
                    <SectionImageMenuItem
                        sectionId={props.section.id}
                        photo={props.section.photo}
                        close={handleClose}
                        key="set-image"
                    />,
                    <DeleteMenuItem onDeleteClick={handleDeleteClick} key="delete-dish-item" />
                );
                return content;
            }
        }
    }

    return (
        <div>
            <IconButton aria-controls="customized-menu" aria-haspopup="true" onClick={handleClick}>
                <MoreVertIcon style={{ color: theme.palette.common.white }} />
            </IconButton>
            <StyledMenu
                id="customized-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
            >
                {prepareContent()}
            </StyledMenu>
            <PopupConfirmation
                open={showConfirmation}
                close={() => setShowConfirmation(false)}
                action={props.onDeleteSectionClick}
                title={prepareDeleteTitle()}
                description={prepareConfirmationMessage()}
                activeBtn={i18n.t('button.delete')}
            />
            <ColorSelector
                isOpen={showColorSelector}
                onClose={() => setShowColorSelector(false)}
                value={props.color}
                onChange={props.onChangeColor}
                onSave={props.onSaveColor}
                onCancel={handleColorReset}
            />
        </div>
    );
}
